<template lang="pug">
app-main-content
  .c-resources-page
    //- Header Section
    section.c-resources-page__header
      .c-resources-page__header-content
        h1.c-resources-page__heading Resources
        p.c-resources-page__subtitle Manage todos and workflows

    //- Tab Navigation
    section.c-resources-page__nav-section(v-if="$route.path !== '/manage-resources'")
      .c-resources-page__nav-wrapper
        .c-resources-nav
          router-link.c-resources-nav-link(
            to="/manage-resources/todos"
            :class="{ 'c-resources-nav-link--active': $route.path.includes('/todos') }"
          ) Todos
          router-link.c-resources-nav-link(
            to="/manage-resources/workflows"
            :class="{ 'c-resources-nav-link--active': $route.path.includes('/workflows') }"
          ) Workflows
        
        .c-resources-page__nav-actions
          #resource-actions-portal

    //- Empty State
    template(v-if="$route.path === '/manage-resources'")
      .c-resources-empty
        p.c-resources-empty__text Select a section from above to manage resources

    //- Content
    router-view(v-slot="{ Component }" v-else)
      transition(name="fade" mode="out-in")
        component(:is="Component")
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'ManageResources',
});
</script>

<style lang="scss" scoped>
// Color Variables
$color-teal-accent: #01beb0;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-300: #d1d5db;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$gray-900: #111827;
$color-purple-text: #4349dd;

.c-resources-page {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 2rem;
    margin-bottom: 0;
  }

  &__header-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__heading {
    font-size: 1.875rem;
    font-weight: 600;
    color: $gray-900;
    margin: 0;
    line-height: 1.2;
  }

  &__subtitle {
    font-size: 0.875rem;
    color: $gray-600;
    margin: 0;
    line-height: 1.5;
  }

  &__nav-section {
    margin-top: 0;
    margin-bottom: 0;
  }

  &__nav-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
  }

  &__nav-actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
}

// Navigation Tabs (Pill Style) - Match Directory/Stickers page
.c-resources-nav {
  display: inline-flex;
  height: 44px;
  align-items: center;
  justify-content: center;
  background-color: $gray-100;
  border-radius: 12px;
  padding: 4px;
  gap: 0;
  flex-wrap: wrap;
  overflow-x: auto;
}

.c-resources-nav-link {
  display: inline-flex;
  height: calc(100% - 2px);
  align-items: center;
  justify-content: center;
  padding: 6px 24px;
  border-radius: 10px;
  font-size: 0.9375rem;
  font-weight: 600;
  color: $gray-600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  user-select: none;

  &:hover {
    color: $gray-900;
    background-color: rgba(255, 255, 255, 0.5);
  }

  &--active {
    background-color: white;
    color: $gray-900;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  }
}

// Empty State
.c-resources-empty {
  background: white;
  border-radius: 16px;
  border: 1px solid #dfdfdf;
  padding: 4rem 2rem;
  text-align: center;
  margin-top: 1rem;

  &__text {
    font-size: 0.9375rem;
    color: $gray-500;
    margin: 0;
  }
}

// Fade transition
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

// Responsive Design
@media (max-width: 768px) {
  .c-resources-page {
    padding: 1.5rem;

    &__heading {
      font-size: 1.5rem;
    }

    &__subtitle {
      font-size: 0.8125rem;
    }
  }

  .c-resources-page {
    &__nav-wrapper {
      flex-direction: column;
      align-items: flex-start;
    }

    &__nav-actions {
      width: 100%;
    }
  }

  .c-resources-nav {
    width: 100%;
  }

  .c-resources-nav-link {
    font-size: 0.875rem;
    padding: 6px 16px;
  }
}

@media (max-width: 480px) {
  .c-resources-page {
    padding: 1rem;

    &__heading {
      font-size: 1.25rem;
    }

    &__subtitle {
      font-size: 0.75rem;
    }
  }
}
</style>
